import {
  AfterViewInit,
  Component,
  Input,
  OnInit,
  OnDestroy,
  ElementRef,
  ViewChild,
  Renderer2,
} from "@angular/core";
import { TranslateService } from "@ngx-translate/core";

import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  Validators,
} from "@angular/forms";

import { BehaviorSubject, Subject, Subscription } from "rxjs";
import { auditTime, takeUntil } from "rxjs/operators";

import * as Leaflet from "leaflet";
import Swal from "sweetalert2";

import { EventService } from "src/app/core/services/event.service";
import { PropertiesService } from "../../services/properties.services";
import { PermissionsService } from "src/app/core/services/permissions.service";
import { PropertyDataService } from "src/app/core/services/property-data.service";
import { PropertyValidationRulesService } from "src/app/core/services/propertyValidationRules.service";
@Component({
  selector: "app-address",
  templateUrl: "./address.component.html",
  styleUrls: ["./address.component.scss"],
})
export class AddressComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() addressForm: any;
  @Input() dataToFormInitAddress: any;
  @Input() selectedStore: any;
  @Input() stores: any[] = [];
  @Input() property: any;
  @Input() submitted: boolean = false;

  propertyAddressForm!: FormGroup;

  options: any;
  map!: Leaflet.Map;
  markers: Leaflet.Marker[] = [];

  countries: any = [];
  regions: any = [];
  districts: any = [];
  counties: any = [];
  parishes: any = [];
  lat: number = 0;
  lon: number = 0;

  subscription!: Subscription;
  subscriptionSubmitted!: Subscription;
  aiExtractedSub?: Subscription;

  loadSubject$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  isView$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  postalCodeFormat = "0000-000";

  @ViewChild("formElementRef") formElementRef!: ElementRef;
  portugalCountry: any;

  defaultValues = {
    country: null,
    district: null,
    county: null,
  };

  private destroy$ = new Subject<void>();

  isLocationChanged = false;
  private isApplyingAiPatch = false;

  /**
   * Coordenadas à mão: raras, por isso ficam atrás de um link no cabeçalho do
   * mapa. Se estivessem sempre à vista pareciam mais um campo a preencher.
   *
   * Fica sempre fechado ao abrir: não há forma de distinguir uma coordenada
   * escrita à mão de uma que veio do Nominatim ou do marcador.
   */
  showManualCoordinates = false;
  manualCoordsInvalid = false;
  private coordsBeforeEdit: { latitude: any; longitude: any } | null = null;

  constructor(
    private eventService: EventService,
    private formBuilder: FormBuilder,
    private propertiesService: PropertiesService,
    private renderer: Renderer2,
    private permissionsService: PermissionsService,
    private translateService: TranslateService,
    private propertyData: PropertyDataService,
    private propertyValidationService: PropertyValidationRulesService,
  ) { }

  ngOnInit(): void {
    this.subscription = new Subscription();

    this.countries = this.dataToFormInitAddress.countries;
    if (this.countries)
      this.portugalCountry = this.countries?.find(
        (c: any) => c.name === "Portugal",
      );

    this.regions = this.dataToFormInitAddress?.regions;
    this.districts = this.dataToFormInitAddress?.districts;
    this.counties = this.dataToFormInitAddress?.counties;
    this.parishes = this.filterActiveParishes(
      this.dataToFormInitAddress?.parishes,
      this.addressForm?.data?.parish,
    );

    this.setDefaults();

    if (this.addressForm?.data?.latitude && this.addressForm?.data?.longitude) {
      this.dataToFormInitAddress.latitude = this.addressForm.data.latitude;
      this.dataToFormInitAddress.longitude = this.addressForm.data.longitude;
    }

    if (this.addressForm?.data?.country) {
      this.changeCountry({ id: this.addressForm?.data?.country }, false);
    }

    if (this.addressForm?.data?.region) {
      this.changeRegion({ id: this.addressForm?.data?.region }, false);
    }

    if (this.addressForm?.data?.district) {
      this.changeDistrict({ id: this.addressForm?.data?.district }, false);
    }

    if (this.addressForm?.data?.county) {
      this.changeCounty({ id: this.addressForm?.data?.county }, false);
    }

    this.loadSubject$.asObservable().subscribe((val) => {
      if (val === 4) {
        this.initFormValidation(this.addressForm);

        if (this.propertyAddressForm) {
          this.propertyAddressForm.valueChanges
            .pipe(auditTime(150))
            .subscribe((val) => this.updatePropertyData(val));

          this.updatePropertyData(this.propertyAddressForm.getRawValue());
        }

        this.initMap();
        this.isView$.next(true);
      }
    });

    this.propertyValidationService
      .rules$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((map) => {
        if (!map?.size || !this.propertyAddressForm) return;

        const revalidateTree = (ctrl: AbstractControl) => {
          if (ctrl instanceof FormGroup) {
            Object.values(ctrl.controls).forEach(revalidateTree);
          } else if (ctrl instanceof FormArray) {
            ctrl.controls.forEach(revalidateTree);
          }
          ctrl.updateValueAndValidity({ emitEvent: false });
        };

        revalidateTree(this.propertyAddressForm);
      });
  }

  ngAfterViewInit(): void {
    this.isView$.asObservable().subscribe((val) => {
      if (val) {
        this.subscriptionSubmitted = this.eventService.subscribe(
          "get-data-tab-address",
          () => {
            this.addressForm.data = this.mergeAddressForSaveSnapshot(
              this.propertyAddressForm.value,
              this.addressForm?.data,
            );
            this.addressForm.errors = this.getFormErrors();
            this.addressForm.valid = this.propertyAddressForm.valid;
            this.eventService.broadcast("check-warning-add-property");
          },
        );

        this.propertyAddressForm.get("country")?.valueChanges.subscribe(() => {
          this.eventService.broadcast("get-data-tab-address");
        });
        this.propertyAddressForm.get("region")?.valueChanges.subscribe(() => {
          this.eventService.broadcast("get-data-tab-address");
        });
        this.propertyAddressForm.get("district")?.valueChanges.subscribe(() => {
          this.eventService.broadcast("get-data-tab-address");
        });
        this.propertyAddressForm.get("county")?.valueChanges.subscribe(() => {
          this.eventService.broadcast("get-data-tab-address");
        });
        this.propertyAddressForm.get("parish")?.valueChanges.subscribe(() => {
          this.eventService.broadcast("get-data-tab-address");
        });
        this.propertyAddressForm.get("address")?.valueChanges.subscribe(() => {
          this.eventService.broadcast("get-data-tab-address");
        });
        this.propertyAddressForm.get("zipCode")?.valueChanges.subscribe(() => {
          this.eventService.broadcast("get-data-tab-address");
        });
        this.propertyAddressForm.get("zone")?.valueChanges.subscribe(() => {
          this.eventService.broadcast("get-data-tab-address");
        });

        setTimeout(() => {
          window.dispatchEvent(new Event("resize"));
          this.submitted = true;
          setTimeout(() => {
            this.setEletemtConfig();
          });
        });
      }
    });

    this.aiExtractedSub = this.eventService.subscribe("ai-doc-extracted", (data: any) => {
      this.applyAiExtraction(data);
    });
  }

  private mergeAddressForSaveSnapshot(currentValue: any, fallbackValue: any) {
    const current = currentValue ?? {};
    const fallback = fallbackValue ?? {};
    const merged = { ...current };

    const keysToPreserve = [
      "country",
      "region",
      "district",
      "county",
      "parish",
      "address",
      "zipCode",
      "zone",
      "doorNumber",
      "fraction",
      "floor",
      "latitude",
      "longitude",
      "districtName",
      "countyName",
      "parishName",
    ];

    const hasValue = (value: any) =>
      value !== null &&
      value !== undefined &&
      !(typeof value === "string" && value.trim() === "");

    for (const key of keysToPreserve) {
      if (!hasValue(merged[key]) && hasValue(fallback[key])) {
        merged[key] = fallback[key];
      }
    }

    return merged;
  }

  private applyAiExtraction(data: any) {
    if (!data || !this.propertyAddressForm) return;

    const patch: any = {};
    const animatedFields: string[] = [];

    const location = this.resolveLocation(data);
    if (location) {
      this.regions = location.country.regions ?? [];
      this.districts = location.region.districts ?? [];
      this.counties = location.district.counties ?? [];
      this.parishes = location.county.parishs ?? [];

      patch.country = location.country.id;
      patch.region = location.region.id;
      patch.district = location.district.id;
      patch.county = location.county.id;
      patch.parish = location.parish.id;
      animatedFields.push("country", "region", "district", "county", "parish");
    }

    if (data.address) {
      patch.address = data.address;
      animatedFields.push("address");
    }
    if (data.zipCode) {
      patch.zipCode = data.zipCode;
      animatedFields.push("zipCode");
    }
    if (data.doorNumber) {
      patch.doorNumber = data.doorNumber;
      animatedFields.push("doorNumber");
    }
    if (data.floor) {
      patch.floor = data.floor;
      animatedFields.push("floor");
    }
    if (data.fraction) {
      patch.fraction = data.fraction;
      animatedFields.push("fraction");
    }
    if (data.latitude !== undefined && data.latitude !== null) {
      patch.latitude = data.latitude;
      animatedFields.push("latitude");
    }
    if (data.longitude !== undefined && data.longitude !== null) {
      patch.longitude = data.longitude;
      animatedFields.push("longitude");
    }

    if (Object.keys(patch).length === 0) return;

    this.isApplyingAiPatch = true;
    this.propertyAddressForm.patchValue(patch, { emitEvent: false });
    this.addressForm.data = this.propertyAddressForm.value;
    this.addressForm.errors = this.getFormErrors();
    this.addressForm.valid = this.propertyAddressForm.valid;
    this.eventService.broadcast("check-warning-add-property");
    this.eventService.broadcast("get-data-tab-address");

    setTimeout(() => {
      this.isApplyingAiPatch = false;
    });

    this.highlightAiFields(animatedFields);
  }

  private resolveLocation(data: any): any {
    const byId = this.resolveLocationByIds(data?.locationMatch);
    if (byId) return byId;

    const wantedDistrict = this.normalizeLocationText(
      this.resolveExtractionLocationName(data, "district"),
    );
    const wantedCounty = this.normalizeLocationText(
      this.resolveExtractionLocationName(data, "county"),
    );
    const wantedParish = this.normalizeLocationText(
      this.resolveExtractionLocationName(data, "parish"),
    );

    if (!wantedDistrict && !wantedCounty && !wantedParish) return null;

    let best:
      | {
          score: number;
          country: any;
          region: any;
          district: any;
          county: any;
          parish: any;
        }
      | null = null;

    for (const country of this.countries ?? []) {
      for (const region of country.regions ?? []) {
        for (const district of region.districts ?? []) {
          const districtScore = wantedDistrict
            ? this.scoreLocationMatch(this.normalizeLocationText(district?.name), wantedDistrict)
            : wantedCounty || wantedParish
              ? 30
              : 0;
          if (districtScore <= 0) continue;
          for (const county of district.counties ?? []) {
            const countyScore = wantedCounty
              ? this.scoreLocationMatch(this.normalizeLocationText(county?.name), wantedCounty)
              : wantedParish
                ? 10
                : 0;
            if (wantedCounty && countyScore <= 0) continue;
            for (const parish of county.parishs ?? []) {
              const parishScore = wantedParish
                ? this.scoreLocationMatch(this.normalizeLocationText(parish?.name), wantedParish)
                : 0;
              if (wantedParish && parishScore <= 0) continue;

              const score = districtScore + countyScore + parishScore;
              if (!best || score > best.score) {
                best = { score, country, region, district, county, parish };
              }
            }
          }
        }
      }
    }

    return best
      ? {
          country: best.country,
          region: best.region,
          district: best.district,
          county: best.county,
          parish: best.parish,
        }
      : null;
  }

  private resolveExtractionLocationName(
    data: any,
    kind: "district" | "county" | "parish",
  ): string | undefined {
    const key = `${kind}Name`;
    const topLevel = data?.[key];
    const nestedAddress = data?.address?.[key];
    const fromMatch = data?.locationMatch?.[kind]?.name;

    const candidate = [topLevel, nestedAddress, fromMatch].find((value) => {
      if (value === null || value === undefined) return false;
      return String(value).trim().length > 0;
    });

    return candidate ? String(candidate) : undefined;
  }

  private resolveLocationByIds(locationMatch: any): any | null {
    const districtId = Number(locationMatch?.district?.id || 0);
    const countyId = Number(locationMatch?.county?.id || 0);
    const parishId = Number(locationMatch?.parish?.id || 0);
    if (!districtId) return null;

    for (const country of this.countries ?? []) {
      for (const region of country.regions ?? []) {
        const district = (region.districts ?? []).find((item: any) => item?.id === districtId);
        if (!district) continue;
        const county =
          (countyId
            ? (district.counties ?? []).find((item: any) => item?.id === countyId)
            : undefined)
          ?? (district.counties ?? []).find((item: any) => item?.name === "---")
          ?? (district.counties ?? [])[0];
        if (!county) continue;

        const parish =
          (parishId
            ? (county.parishs ?? []).find((item: any) => item?.id === parishId)
            : undefined)
          ?? (county.parishs ?? []).find((item: any) => item?.name === "---")
          ?? (county.parishs ?? [])[0];
        if (!parish) continue;

        return { country, region, district, county, parish };
      }
    }

    return null;
  }

  private normalizeLocationText(value: any): string {
    return String(value ?? "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[.,/\\-]/g, " ")
      .replace(/\b(de|da|do|das|dos|e)\b/gi, " ")
      .replace(/\s+/g, " ")
      .toLowerCase()
      .trim();
  }

  private scoreLocationMatch(candidate: string, wanted: string): number {
    if (!candidate || !wanted) return 0;
    if (candidate === wanted) return 100;
    if (candidate.includes(wanted) || wanted.includes(candidate)) return 85;

    const candidateTokens = candidate.split(" ").filter(Boolean);
    const wantedTokens = wanted.split(" ").filter(Boolean);
    if (!candidateTokens.length || !wantedTokens.length) return 0;

    const matches = wantedTokens.filter((token) => candidateTokens.includes(token)).length;
    const ratio = matches / Math.max(wantedTokens.length, candidateTokens.length);
    return Math.round(ratio * 75);
  }

  private highlightAiFields(controlNames: string[]) {
    if (!this.formElementRef?.nativeElement) return;

    for (const name of controlNames) {
      const el = this.formElementRef.nativeElement.querySelector(`[formcontrolname="${name}"]`);
      if (!el) continue;
      this.renderer.addClass(el, "ai-field-fill");
      setTimeout(() => this.renderer.removeClass(el, "ai-field-fill"), 1600);
    }
  }

  updatePropertyData(data: any) {
    const val = {
      address: data?.address,
      districtName: data?.district
        ? this.districts?.find((c: any) => c.id === data?.district)?.name
        : "",
      countyName: data?.county
        ? this.counties?.find((c: any) => c.id === data?.county)?.name
        : "",
      parishName: data?.parish
        ? this.parishes?.find((c: any) => c.id === data?.parish)?.name
        : "",
      zipCode: data?.zipCode,
      zone: data?.zone ?? "",
      doorNumber: data?.doorNumber ?? "",
      fraction: data?.fraction ?? "",
      floor: data?.floor ?? "",
    };
    this.propertyData.updateTab("address", val);
  }

  setDefaults() {
    const c = this.countries?.find((c: any) => c.name === "---");
    const d = this.districts?.find((c: any) => c.name === "---");
    const co = this.counties?.find((c: any) => c.name === "---");

    this.defaultValues = {
      country: c?.id,
      district: d?.id,
      county: co?.id,
    };
  }

  changeCountry(event: any, isChange = true) {
    if (event?.id) {
      const subR = this.propertiesService
        .getDataForAddressRegions(event.id)
        .subscribe((data) => {
          this.regions = data;
          const country = this.countries.find((c: any) => c.id === event?.id);
          this.postalCodeFormat = country?.postalCodeFormat
            ? country?.postalCodeFormat
            : "0000-000";
          if (isChange && !this.isApplyingAiPatch) {
            this.propertyAddressForm.get("region")?.reset();
            const r = this.regions.find((c: any) => c.name === "---");
            this.propertyAddressForm.patchValue({ region: r.id });
            this.changeRegion(r);
            this.setDefaults();
            this.form["zipCode"].setValue(null);
          }
          let val = this.loadSubject$.getValue();
          if (val < 4) {
            val++;
            this.loadSubject$.next(val);
          }
        });
      this.subscription.add(subR);
    }
  }

  changeRegion(event: any, isChange = true) {
    if (event?.id) {
      const subD = this.propertiesService
        .getDataForAddressDistrict(event.id)
        .subscribe((data) => {
          this.districts = data;
          if (isChange && !this.isApplyingAiPatch) {
            this.propertyAddressForm.get("district")?.reset();
            const d = this.districts.find((c: any) => c.name === "---");
            this.propertyAddressForm.patchValue({ district: d.id });
            this.changeDistrict(d);
            this.setDefaults();
          }
          let val = this.loadSubject$.getValue();
          if (val < 4) {
            val++;
            this.loadSubject$.next(val);
          }
        });
      this.subscription.add(subD);
    }
  }

  changeDistrict(event: any, isChange = true) {
    if (event?.id) {
      const subCO = this.propertiesService
        .getDataForAddressCounties(event.id)
        .subscribe((data) => {
          this.counties = data;
          if (isChange && !this.isApplyingAiPatch) {
            this.propertyAddressForm.get("county")?.reset();
            const co = this.counties.find((c: any) => c.name === "---");
            this.propertyAddressForm.patchValue({ county: co.id });
            this.changeCounty(co);
            this.setDefaults();
          }
          let val = this.loadSubject$.getValue();
          if (val < 4) {
            val++;
            this.loadSubject$.next(val);
          }
        });
      this.subscription.add(subCO);
    }
  }

  changeCounty(event: any, isChange = true) {
    if (event?.id) {
      const subP = this.propertiesService
        .getDataForAddressParish(event.id)
        .subscribe((data) => {
          this.parishes = this.filterActiveParishes(
            data,
            isChange ? null : this.addressForm?.data?.parish,
          );
          if (isChange && !this.isApplyingAiPatch) {
            this.propertyAddressForm.get("parish")?.reset();
            const p = this.parishes.find((c: any) => c.name === "---");
            this.propertyAddressForm.patchValue({ parish: p.id });
            this.autoRefreshGeoreference();
          }
          let val = this.loadSubject$.getValue();
          if (val < 4) {
            val++;
            this.loadSubject$.next(val);
          }
        });
      this.subscription.add(subP);
    }
  }

  // Esconde freguesias desativadas, exceto a atualmente gravada no imóvel —
  // essa fica disabled (e oculta via CSS) para mostrar o valor sem ser re-selecionável
  private filterActiveParishes(list: any[], keepId: any = null): any[] {
    return (list || [])
      .filter((p: any) => p?.status !== false || p.id == keepId)
      .map((p: any) => (p?.status === false ? { ...p, disabled: true } : p));
  }

  get showOutdatedParishNotice(): boolean {
    const createdAt = this.addressForm?.data?.propertyCreatedAt;
    if (!createdAt) return false;
    const parishId =
      this.propertyAddressForm?.get("parish")?.value ??
      this.addressForm?.data?.parish;
    const selected = this.parishes?.find((p: any) => p.id == parishId);
    const status = selected
      ? selected.status
      : this.addressForm?.data?.parishStatus;
    const deactivatedAt = selected
      ? selected.deactivatedAt
      : this.addressForm?.data?.parishDeactivatedAt;
    if (status !== false || !deactivatedAt) return false;
    return new Date(createdAt) > new Date(deactivatedAt);
  }

  /**
   * Form data get
   */
  get form() {
    return this.propertyAddressForm.controls;
  }

  /**
   * Form Validation
   */
  initFormValidation(addressForm: any) {
    const warn = (
      ruleKey: string,
      control: (c: AbstractControl) => boolean,
    ) => {
      return this.propertyValidationService.warningIfEnabled(ruleKey, control);
    };

    const validate = (arr: any[]) => arr?.find((p: any) => p.name === "---");

    const counstries = validate(this.countries);
    const regions = validate(this.regions);
    const districts = validate(this.districts);
    const counties = validate(this.counties);
    const parishes = validate(this.parishes);

    this.propertyAddressForm = this.formBuilder.group({
      country: [
        addressForm?.data?.country,
        [
          Validators.required,
          warn("address.country", (c) => {
            const p = this.countries?.find((p: any) => p.id == c?.value);
            if (p && p.name === "---") {
              return true;
            }
            return false;
          }),
        ],
      ],

      region: [
        addressForm?.data?.region,
        [
          Validators.required,
          warn("address.region", (c) => {
            const p = this.regions?.find((p: any) => p.id == c?.value);
            if (p && p.name === "---") {
              return true;
            }
            return false;
          }),
        ],
      ],
      district: [
        addressForm?.data?.district,
        [
          Validators.required,
          warn("address.district", (c) => {
            const p = this.districts?.find((p: any) => p.id == c?.value);
            if (p && p.name === "---") {
              return true;
            }
            return false;
          }),
        ],
      ],
      county: [
        addressForm?.data?.county,
        [
          Validators.required,
          warn("address.county", (c) => {
            const p = this.counties?.find((p: any) => p.id == c?.value);
            if (p && p.name === "---") {
              return true;
            }
            return false;
          }),
        ],
      ],
      parish: [
        addressForm?.data?.parish,
        [
          Validators.required,
          warn("address.parish", (c) => {
            const p = this.parishes?.find((p: any) => p.id == c?.value);
            if (p && p.name === "---") {
              return true;
            }
            return false;
          }),
        ],
      ],

      address: [
        addressForm?.data?.address,
        [warn("address.address", (c) => !c?.value)],
      ],
      zipCode: [
        addressForm?.data?.zipCode,
        [warn("address.zipCode", (c) => !c?.value)],
      ],
      zone: [addressForm?.data?.zone ?? ""],

      doorNumber: [addressForm?.data?.doorNumber],
      fraction: [addressForm?.data?.fraction],
      floor: [addressForm?.data?.floor],
      latitude: [
        addressForm?.data?.latitude
          ? addressForm?.data?.latitude
          : this.dataToFormInitAddress?.latitude,
      ],
      longitude: [
        addressForm?.data?.longitude
          ? addressForm?.data?.longitude
          : this.dataToFormInitAddress?.longitude,
      ],
      hidePropertyAddress: [addressForm?.data?.hidePropertyAddress === true],
      hideMap: [addressForm?.data?.hideMap === true],
      hideDistrict: [addressForm?.data?.hideDistrict === true],
      hideCounty: [addressForm?.data?.hideCounty === true],
      hideParish: [addressForm?.data?.hideParish === true],
    });

    this.showManualCoordinates = false;
    this.manualCoordsInvalid = false;
  }

  /**
   * Get form errors
   */
  getFormErrors(): [] {
    let errors: any = [];
    Object.keys(this.propertyAddressForm.controls).forEach((key) => {
      // Get errors of every form control
      let type = this.propertyAddressForm?.get(key)?.errors;

      if (type && !type["mask"]) errors.push({ key, type });
    });
    return errors;
  }

  initMap() {
    /**
     * Basic Maps
     */
    this.options = {
      layers: [
        Leaflet.tileLayer(
          "https://api.mapbox.com/styles/v1/{id}/tiles/{z}/{x}/{y}?access_token=pk.eyJ1IjoiZGV2ZGF0YWRlYnVnIiwiYSI6ImNsaWx1OGczaDA2OHEzcHB2aTVxd2Y3YjIifQ.si2wX403lhDnztGcAzBdLA",
          {
            maxZoom: 18,
            attribution:
              'Map data &copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors, <a href="https://creativecommons.org/licenses/by-sa/2.0/">CC-BY-SA</a>, Imagery © <a href="https://www.mapbox.com/">Mapbox</a>',
            id: "mapbox/streets-v11",
            tileSize: 512,
            zoomOffset: -1,
          },
        ),
      ],
      zoom: 12,
      doubleClickZoom: false,
      center: {
        lat: this.addressForm?.data?.latitude
          ? this.addressForm?.data?.latitude
          : this.dataToFormInitAddress?.latitude,
        lng: this.addressForm?.data?.longitude
          ? this.addressForm?.data?.longitude
          : this.dataToFormInitAddress?.longitude,
      },
    };
  }

  initMarkers() {
    const initialMarkers = [
      {
        position: {
          lat: this.addressForm?.data?.latitude
            ? this.addressForm?.data?.latitude
            : this.dataToFormInitAddress?.latitude,
          lng: this.addressForm?.data?.longitude
            ? this.addressForm?.data?.longitude
            : this.dataToFormInitAddress?.longitude,
        },
        draggable: true,
      },
    ];
    for (let index = 0; index < initialMarkers.length; index++) {
      const data = initialMarkers[index];
      const marker = this.generateMarker(data, index);
      marker.addTo(this.map);
      this.map.panTo(data.position);
      this.markers.push(marker);
    }
  }

  generateMarker(data: any, index: number) {
    return Leaflet.marker(data.position, {
      draggable: data.draggable,
      icon: Leaflet.icon({
        iconSize: [24, 34],
        iconAnchor: [12, 24],
        // specify the path here
        iconUrl: "assets/images/marker-icon-2x.png",
        shadowUrl: "assets/images/marker-shadow.png",
      }),
    }).on("dragend", (event) => this.markerDragEnd(event, index));
  }

  onMapReady($event: Leaflet.Map) {
    this.map = $event;
    this.initMarkers();
  }

  mapDblClick($event: any) {
    this.markers[0].setLatLng($event.latlng);
    this.propertyAddressForm.patchValue({
      latitude: $event.latlng.lat,
      longitude: $event.latlng.lng,
    });
    this.isLocationChanged = true;
  }

  toggleManualCoordinates() {
    this.showManualCoordinates = !this.showManualCoordinates;

    if (this.showManualCoordinates) this.manualCoordsInvalid = false;
  }

  /**
   * Guarda o par que lá estava antes de o utilizador começar a escrever, para
   * o poder repor. Com um valor já inválido no formulário não se sobrepõe o
   * que foi guardado — senão passar de um campo para o outro perdia-o.
   */
  onManualCoordinateFocus() {
    if (this.manualCoordsInvalid) return;

    this.coordsBeforeEdit = {
      latitude: this.form["latitude"]?.value ?? null,
      longitude: this.form["longitude"]?.value ?? null,
    };
  }

  /**
   * Coordenadas escritas à mão: valida o par e, se servir, leva o marcador lá.
   *
   * Marca `isLocationChanged` pela mesma razão que o arrastar do marcador —
   * é o que impede o `autoRefreshGeoreference()` de voltar a sobrepor as
   * coordenadas na próxima vez que se mexe na morada.
   */
  onManualCoordinateChange() {
    const latitude = Number(this.form["latitude"]?.value);
    const longitude = Number(this.form["longitude"]?.value);

    if (!this.isValidCoordinatePair(latitude, longitude)) {
      this.manualCoordsInvalid = true;
      return;
    }

    this.manualCoordsInvalid = false;
    this.isLocationChanged = true;

    if (!this.map || !this.markers?.length) return;

    this.markers[0].setLatLng({ lat: latitude, lng: longitude });
    this.map.panTo({ lat: latitude, lng: longitude });
  }

  /** Sair do campo com um valor impossível repõe o que lá estava antes. */
  onManualCoordinateBlur() {
    if (!this.manualCoordsInvalid) return;

    this.propertyAddressForm.patchValue({
      latitude: this.coordsBeforeEdit?.latitude ?? null,
      longitude: this.coordsBeforeEdit?.longitude ?? null,
    });
    this.manualCoordsInvalid = false;
  }

  private isValidCoordinatePair(latitude: number, longitude: number): boolean {
    return (
      Number.isFinite(latitude) &&
      Number.isFinite(longitude) &&
      latitude >= -90 &&
      latitude <= 90 &&
      longitude >= -180 &&
      longitude <= 180
    );
  }

  markerDragEnd($event: any, index: number) {
    const geo = $event.target.getLatLng();
    this.propertyAddressForm.patchValue({
      latitude: geo.lat,
      longitude: geo.lng,
    });
    this.isLocationChanged = true;
  }

  setEletemtConfig() {
    if (
      !this.permissionsService.checkMasterPermission(
        "EDIT_PROPERTIES",
        this.property?.store?.id,
      ) &&
      !this.permissionsService.checkAgentPermission(
        this.property?.mainAgent,
        "EDIT_PROPERTIES",
      ) &&
      this.property
    ) {
      if (this.formElementRef.nativeElement) {
        this.renderer.setStyle(
          this.formElementRef.nativeElement,
          "pointer-events",
          "none",
        );
        const parentElement = this.formElementRef.nativeElement.parentElement;
        if (parentElement)
          this.renderer.setStyle(parentElement, "cursor", "not-allowed");
        const warning = parentElement.querySelectorAll(".is-warning");
        if (warning) {
          warning?.forEach((el: any) => {
            this.renderer.removeClass(el, "is-warning");
          });
        }
      }
    }
  }

  validateLocationBtn() {
    if (
      !this.form["country"]?.value ||
      this.form["country"]?.value == this.defaultValues.country
    )
      return false;
    if (
      !this.form["district"]?.value ||
      this.form["district"]?.value == this.defaultValues.district
    )
      return false;
    if (
      !this.form["county"]?.value ||
      this.form["county"]?.value == this.defaultValues.county
    )
      return false;
    if (
      this.form["country"]?.value &&
      this.form["country"]?.value != this.defaultValues.country &&
      this.form["country"]?.value == this.portugalCountry?.id
    ) {
      if (!this.form["address"].value) return false;
      if (!this.form["zipCode"].value) return false;
      return true;
    } else {
      if (!this.form["address"].value) return false;
      return true;
    }
  }

  autoRefreshGeoreference() {
    if (!this.isLocationChanged && !this.property) {
      this.refreshGeoreference(true);
    }
  }

  refreshGeoreference(isAuto = false) {
    if (this.form["country"]?.value == this.portugalCountry?.id) {
      if (
        this.form["country"].value &&
        this.form["district"].value &&
        this.form["county"].value 
        // this.form["address"].value &&
        // (this.form["country"].value != this.portugalCountry.id ||
        //   this.form["zipCode"].value)
      ) {
        const country = this.countries.find(
          (c: any) => c.id === this.form["country"].value,
        );
        const district = this.districts.find(
          (d: any) => d.id === this.form["district"].value,
        );
        const county = this.counties.find(
          (c: any) => c.id === this.form["county"].value,
        );
        if (!county || county.name === "---") return;
        const parish = this.parishes.find(
          (p: any) => p.id === this.form["parish"].value,
        );
        this.propertiesService
          .refreshGeoreference(
            country.name,
            district.name,
            county.name,
            this.form["address"].value,
            this.form["zipCode"]?.value,
            parish?.name && parish.name !== "---" ? parish.name : undefined
          )
          .subscribe({
            next: (data) => {
              if (data && data.length > 0) {
                const lat = data[0].lat;
                const lon = data[0].lon;
                if (lat && lon) {
                  this.markers[0].setLatLng({
                    lat: Number(lat),
                    lng: Number(lon),
                  });
                  this.map.setView({ lat: Number(lat), lng: Number(lon) });
                  this.propertyAddressForm.patchValue({
                    latitude: Number(lat),
                    longitude: Number(lon),
                  });
                }
              } else {
                if (!isAuto) {
                  Swal.fire(
                    this.translateService.instant("t-georeference-no-result"),
                    "",
                    "warning",
                  );
                }
              }
            },
          });
      }
    } else if (
      this.form["country"]?.value &&
      this.form["country"]?.value != this.portugalCountry?.id
    ) {
      if (
        this.form["country"].value &&
        this.form["district"].value &&
        this.form["county"].value &&
        this.form["address"].value
      ) {
        const country = this.countries.find(
          (c: any) => c.id === this.form["country"].value,
        );
        const district = this.districts.find(
          (d: any) => d.id === this.form["district"].value,
        );
        const county = this.counties.find(
          (c: any) => c.id === this.form["county"].value,
        );
        this.propertiesService
          .refreshGeoreferenceByQuery(
            country.name,
            district.name,
            county.name,
            this.form["address"].value,
          )
          .subscribe({
            next: (data) => {
              if (data && data.length > 0) {
                const lat = data[0].lat;
                const lon = data[0].lon;
                if (lat && lon) {
                  this.markers[0].setLatLng({
                    lat: Number(lat),
                    lng: Number(lon),
                  });
                  this.map.setView({ lat: Number(lat), lng: Number(lon) });
                  this.propertyAddressForm.patchValue({
                    latitude: Number(lat),
                    longitude: Number(lon),
                  });
                }
              } else {
                this.propertiesService
                  .refreshGeoreferenceByQuery(
                    country.name,
                    district.name,
                    county.name,
                    "",
                  )
                  .subscribe({
                    next: (data) => {
                      if (data && data.length > 0) {
                        const lat = data[0].lat;
                        const lon = data[0].lon;
                        if (lat && lon) {
                          this.markers[0].setLatLng({
                            lat: Number(lat),
                            lng: Number(lon),
                          });
                          this.map.setView({
                            lat: Number(lat),
                            lng: Number(lon),
                          });
                          this.propertyAddressForm.patchValue({
                            latitude: Number(lat),
                            longitude: Number(lon),
                          });
                        }
                      } else {
                        if (!isAuto) {
                          Swal.fire(
                            this.translateService.instant(
                              "t-georeference-no-result",
                            ),
                            "",
                            "warning",
                          );
                        }
                      }
                    },
                  });
              }
            },
          });
      }
    }
  }

  ngOnDestroy(): void {
    this.addressForm.data = this.propertyAddressForm.value;
    this.addressForm.errors = this.getFormErrors();
    this.addressForm.valid = this.propertyAddressForm.valid;
    this.eventService.broadcast("check-warning-add-property");
    this.subscription.unsubscribe();
    this.subscriptionSubmitted.unsubscribe();
    this.aiExtractedSub?.unsubscribe();
  }
}
